import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space, card, btnPrimary } from '../styles/theme.js'
import { projects as projectsApi, stylePresets as stylePresetsApi } from '../lib/api.js'
import { StyleEditor } from '../components/style-editor.js'

const breadcrumb = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  margin-bottom: ${space.lg};
  a { color: ${colors.textSecondary}; }
`

const pageTitle = css`
  font-size: ${font.size['2xl']};
  font-weight: ${font.weight.bold};
  margin-bottom: ${space.md};
`

const pageDesc = css`
  font-size: ${font.size.sm};
  color: ${colors.textSecondary};
  margin-bottom: ${space.xl};
`

const emptyState = css`
  text-align: center;
  padding: ${space.lg};
  color: ${colors.textMuted};
  font-size: ${font.size.sm};
`

export function StylesView() {
  let projectId
  let project = null
  let presets = []
  let loading = true
  let error = null
  let saving = false
  let extracting = false
  let isOwner = false

  async function load() {
    loading = true
    error = null
    try {
      const [proj, list] = await Promise.all([
        projectsApi.get(projectId),
        stylePresetsApi.list(projectId),
      ])
      project = proj
      isOwner = proj.memberRole === 'owner'
      presets = list
    } catch (e) {
      error = e.message
    } finally {
      loading = false
      m.redraw()
    }
  }

  return {
    oninit(vnode) {
      projectId = vnode.attrs.projectId
      load()
    },
    view() {
      if (loading) return m('div', { class: emptyState }, 'Loading...')

      return m('div', [
        m('div', { class: breadcrumb }, [
          m(m.route.Link, { href: '/projects' }, 'Projects'),
          ' / ',
          m(m.route.Link, { href: `/projects/${projectId}` }, project?.name || 'Project'),
          ' / Style Presets',
        ]),

        m('h1', { class: pageTitle }, 'Style Presets'),
        m('p', { class: pageDesc }, 'Manage communication style presets used for scenario generation across all packs.'),

        error ? m('div', {
          style: `color: ${colors.critical}; margin-bottom: ${space.md};`,
        }, error) : null,

        m(StyleEditor, {
          stylePresets: presets,
          readOnly: !isOwner,
          saving,
          extracting,
          onCreatePreset: async (data) => {
            saving = true; m.redraw()
            try {
              await stylePresetsApi.create(projectId, data)
              presets = await stylePresetsApi.list(projectId)
            } catch (e) { error = e.message }
            saving = false; m.redraw()
          },
          onUpdatePreset: async (presetId, data) => {
            saving = true; m.redraw()
            try {
              await stylePresetsApi.update(projectId, presetId, data)
              presets = await stylePresetsApi.list(projectId)
            } catch (e) { error = e.message }
            saving = false; m.redraw()
          },
          onDeletePreset: async (presetId) => {
            saving = true; m.redraw()
            try {
              await stylePresetsApi.delete(projectId, presetId)
              presets = await stylePresetsApi.list(projectId)
            } catch (e) { error = e.message }
            saving = false; m.redraw()
          },
          onExtractStyles: async () => {
            extracting = true; error = null; m.redraw()
            try {
              await stylePresetsApi.extract(projectId)
              presets = await stylePresetsApi.list(projectId)
            } catch (e) { error = e.message }
            extracting = false; m.redraw()
          },
        }),
      ])
    },
  }
}
